// ============================================================================
// GlobalSearch — caja de búsqueda global tipo "command palette".
//
// Dos piezas en un solo módulo (vienen juntas porque comparten estado):
//   • `<GlobalSearchTrigger />` — pill compacto que se monta en la TopBar.
//     Click o Cmd/Ctrl+K abren el modal.
//   • `<GlobalSearchModal />` — overlay full-screen con el input grande y la
//     lista de resultados. Cada resultado lleva un badge con el módulo al que
//     pertenece (Inventario · Carta · Mesas · Pedidos · Reservas · Facturas).
//
// El estado "abierto/cerrado" vive en el componente raíz exportado
// `GlobalSearch`, que solo coloca el trigger y, cuando se abre, monta el
// modal en un portal-equivalente (fixed inset-0). El listener de Cmd+K se
// instala a nivel `document` para que el atajo funcione desde cualquier pantalla.
//
// Re-spec 2-jun-2026 (chat usuario): "búsqueda global que sea fácil, con
// indicador del módulo antes de clickear". El modal sigue el patrón
// Linear/Notion: input arriba, resultados abajo, navegación con flechas.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search as SearchIcon,
  X,
  Box, BookOpen, Users as UsersIcon, ShoppingCart, Calendar, Receipt,
  TrendingDown, Contact,
  CornerDownLeft, ArrowUp, ArrowDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router';
import { SEARCH_MODULE_LABEL, type SearchModule } from '../../context/search-context';
import { useI18n, useTextos } from '../../context/regional-context';
import { textosBusqueda } from '../../../i18n/modulos/busqueda';
import { useUser } from '../../context/user-context';
import { useBranch } from '../../context/branch-context';
import { searchGlobal, type SearchResultItem } from '../../../api/search';

const MODULE_ICON: Record<SearchModule, LucideIcon> = {
  inventory:    Box,
  menu:         BookOpen,
  tables:       UsersIcon,
  orders:       ShoppingCart,
  reservations: Calendar,
  billing:      Receipt,
  purchases:    TrendingDown,
  terceros:     Contact,
};

// Tonos del badge — heredan colores ya definidos en el theme.
const MODULE_TONE: Record<SearchModule, { bg: string; fg: string }> = {
  inventory:    { bg: 'var(--blue-50)',  fg: 'var(--blue-700)' },
  menu:         { bg: 'var(--ok-bg)',    fg: 'var(--ok-ink)' },
  tables:       { bg: 'var(--warn-bg)',  fg: 'var(--warn-ink)' },
  orders:       { bg: '#EDE9FE',         fg: '#6D28D9' },
  reservations: { bg: '#FFE4E6',         fg: '#9F1239' },
  billing:      { bg: 'var(--surface-2)', fg: 'var(--muted-ink)' },
  purchases:    { bg: '#FEF3C7',         fg: '#B45309' },
  terceros:     { bg: '#E0E7FF',         fg: '#3730A3' },
};

// ----------------------------------------------------------------------------
// Trigger compacto — vive en la topbar.
// ----------------------------------------------------------------------------
interface GlobalSearchProps {
  /** Si true, el botón ocupa todo el ancho disponible y baja su altura
   *  para alinearse con otros pills (ej. el selector de sede del topbar). */
  fill?: boolean;
  /** Si true, renderiza solo el ícono de lupa en un botón cuadrado 40×40
   *  con fondo translúcido (estilo "campana" del topbar de cool). Útil para
   *  mobile cuando no hay espacio para la barra completa. */
  iconOnly?: boolean;
}

export function GlobalSearch({ fill = false, iconOnly = false }: GlobalSearchProps = {}) {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();

  // Atajo de teclado global: Cmd+K (mac) / Ctrl+K (otro). Soft-block si el
  // user está escribiendo en un input/textarea para no robar input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(o => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Variante "icon-only": cuadrado 40×40 sobre fondo translúcido (estilo
  // botón de campana del topbar cool). El click sigue abriendo el mismo
  // modal de búsqueda — solo cambia el trigger visual.
  if (iconOnly) {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          aria-label={t('busqueda.global')}
          title={t('busqueda.atajo')}
          className="press"
          style={{
            width: 40, height: 40, borderRadius: 12,
            background: 'rgba(255,255,255,.16)', color: '#fff',
            border: 0, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SearchIcon className="w-[18px] h-[18px]" strokeWidth={2} />
        </button>
        {open && <GlobalSearchModal onClose={() => setOpen(false)} />}
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label={t('busqueda.global')}
        className="press flex items-center"
        style={{
          gap: 10,
          padding: fill ? '5px 14px 5px 14px' : '8px 12px 8px 14px',
          minHeight: fill ? 32 : 38,
          background: 'var(--bg-app)',
          border: '1.5px solid var(--line)',
          borderRadius: 'var(--r-pill)',
          color: 'var(--muted-ink)',
          fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600,
          cursor: 'pointer',
          width: fill ? '100%' : undefined,
          maxWidth: fill ? '100%' : 360,
        }}
      >
        <SearchIcon className="w-[16px] h-[16px]" strokeWidth={2.2} />
        <span className="hidden sm:inline truncate">{t('busqueda.boton')}</span>
        <span className="sm:hidden truncate">{t('busqueda.botonCorto')}</span>
        <span
          className="hidden md:inline-flex items-center"
          style={{
            marginLeft: 'auto',
            gap: 4,
            padding: '2px 7px',
            background: 'var(--surface)',
            border: '1.5px solid var(--line)',
            borderRadius: 6,
            fontSize: 11, fontWeight: 800, color: 'var(--muted-2)',
            letterSpacing: '.04em',
          }}
        >
          ⌘K
        </span>
      </button>
      {open && <GlobalSearchModal onClose={() => setOpen(false)} />}
    </>
  );
}

// ----------------------------------------------------------------------------
// Modal — overlay completo con input + resultados.
// ----------------------------------------------------------------------------
function GlobalSearchModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const tx = useTextos(textosBusqueda);
  const { canViewModule } = useUser();
  const { currentBranchId } = useBranch();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Búsqueda EN VIVO contra el backend (toda la BD, por sede), con debounce.
  // Filtra por permisos del rol: el usuario sólo ve módulos que puede abrir.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchGlobal(currentBranchId, term);
        setResults((r.results || []).filter(e => canViewModule(e.module)));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, currentBranchId, canViewModule]);

  // Reset selection cuando cambia la query.
  useEffect(() => { setIdx(0); }, [q]);

  // Bloquear scroll del body mientras el modal está abierto.
  useEffect(() => {
    document.body.classList.add('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, []);

  // Auto-focus al abrir.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Mantener el ítem activo visible.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row="${idx}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  const go = (e: SearchResultItem) => {
    navigate(e.path);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); const r = results[idx]; if (r) go(r); }
  };

  // Agrupado por módulo para que el activo no se pierda en una lista plana,
  // pero respetando el ranking general (los resultados ya vienen ordenados).
  const grouped = useMemo(() => {
    const m = new Map<SearchModule, SearchResultItem[]>();
    let runningIdx = 0;
    const order: { module: SearchModule; rows: { e: SearchResultItem; globalIdx: number }[] }[] = [];
    for (const e of results) {
      if (!m.has(e.module)) {
        m.set(e.module, []);
        order.push({ module: e.module, rows: [] });
      }
      const bucket = order.find(o => o.module === e.module)!;
      bucket.rows.push({ e, globalIdx: runningIdx++ });
    }
    return order;
  }, [results]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-start justify-center animate-apple-fade-in"
      style={{
        background: 'rgba(11,18,32,.55)',
        backdropFilter: 'blur(8px)',
        padding: 'clamp(40px, 8vh, 90px) 16px 16px',
      }}
    >
      <div
        role="dialog"
        aria-label={t('busqueda.global')}
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col animate-apple-slide-up"
        style={{
          width: '100%', maxWidth: 720, maxHeight: '78vh',
          background: 'var(--surface)',
          borderRadius: 'var(--r-lg)',
          boxShadow: 'var(--sh-lg)',
          overflow: 'hidden',
        }}
      >
        {/* ====== INPUT ====== */}
        <div
          className="flex items-center"
          style={{
            gap: 12, padding: '16px 18px',
            borderBottom: '1.5px solid var(--line)',
          }}
        >
          <SearchIcon
            className="w-[22px] h-[22px] flex-shrink-0"
            style={{ color: 'var(--muted-2)' }}
            strokeWidth={2.2}
          />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('busqueda.placeholder')}
            className="flex-1 focus:outline-none"
            style={{
              border: 0, background: 'transparent',
              fontFamily: 'inherit', fontSize: 17, fontWeight: 600,
              color: 'var(--ink)',
              minWidth: 0,
            }}
          />
          <button
            onClick={onClose}
            aria-label={tx('cerrar')}
            className="press flex items-center justify-center"
            style={{
              width: 32, height: 32, borderRadius: 10,
              background: 'var(--bg-app)', color: 'var(--ink)',
              border: 0, cursor: 'pointer',
            }}
          >
            <X className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>

        {/* ====== RESULTS ====== */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto"
          style={{ padding: '6px 0' }}
        >
          {q.trim().length === 0 && (
            <EmptyHint loading={loading} />
          )}
          {q.trim().length > 0 && results.length === 0 && (
            <div
              className="text-center"
              style={{ padding: '36px 24px', color: 'var(--muted-ink)' }}
            >
              <SearchIcon className="mx-auto w-7 h-7 mb-2" style={{ color: 'var(--muted-2)' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
                {tx('sinResultados', { q })}
              </div>
              <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>
                {tx('probaOtraPalabra')}
              </div>
            </div>
          )}

          {grouped.map(group => (
            <div key={group.module}>
              <div
                style={{
                  padding: '10px 18px 4px',
                  fontSize: 11, fontWeight: 800, letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted-2)',
                }}
              >
                {tx(SEARCH_MODULE_LABEL[group.module])}
                <span
                  className="tabular-nums"
                  style={{
                    marginLeft: 8, fontSize: 11, fontWeight: 700,
                    color: 'var(--muted-ink)',
                    background: 'var(--bg-app)',
                    borderRadius: 999, padding: '1px 7px',
                  }}
                >
                  {group.rows.length}
                </span>
              </div>
              {group.rows.map(({ e, globalIdx }) => (
                <ResultRow
                  key={e.id}
                  e={e}
                  active={globalIdx === idx}
                  onMouseEnter={() => setIdx(globalIdx)}
                  onClick={() => go(e)}
                  rowIndex={globalIdx}
                />
              ))}
            </div>
          ))}
        </div>

        {/* ====== FOOTER hint ====== */}
        <div
          className="flex items-center justify-between"
          style={{
            padding: '10px 18px',
            borderTop: '1.5px solid var(--line)',
            background: 'var(--surface-2)',
            fontSize: 11.5, fontWeight: 700,
            color: 'var(--muted-ink)',
            gap: 12, flexWrap: 'wrap',
          }}
        >
          <Hotkey icon={ArrowUp} label={tx('arriba')} />
          <Hotkey icon={ArrowDown} label={tx('abajo')} />
          <Hotkey icon={CornerDownLeft} label={tx('abrir')} />
          <span style={{ marginLeft: 'auto', opacity: .8 }}>
            {tx('escParaCerrar')}
          </span>
        </div>
      </div>
    </div>
  );
}

function ResultRow({ e, active, onClick, onMouseEnter, rowIndex }: {
  e: SearchResultItem;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  rowIndex: number;
}) {
  const tx = useTextos(textosBusqueda);
  const Icon = MODULE_ICON[e.module];
  const tone = MODULE_TONE[e.module];
  return (
    <div
      data-row={rowIndex}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className="flex items-center cursor-pointer"
      style={{
        gap: 12,
        padding: '10px 18px',
        background: active ? 'var(--blue-50)' : 'transparent',
        transition: 'background .12s',
      }}
    >
      <span
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 34, height: 34, borderRadius: 10,
          background: tone.bg, color: tone.fg,
        }}
      >
        <Icon className="w-[17px] h-[17px]" strokeWidth={2.2} />
      </span>
      <div className="flex-1 min-w-0">
        <div
          className="truncate"
          style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}
        >
          {e.title}
        </div>
        {e.subtitle && (
          <div
            className="truncate"
            style={{
              fontSize: 12.5, fontWeight: 600, color: 'var(--muted-ink)',
              marginTop: 1,
            }}
          >
            {e.subtitle}
          </div>
        )}
      </div>
      <div className="flex items-center" style={{ gap: 10 }}>
        {e.meta && (
          <span
            className="tabular-nums"
            style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted-ink)', whiteSpace: 'nowrap' }}
          >
            {e.meta}
          </span>
        )}
        <span
          className="hidden sm:inline-flex"
          style={{
            padding: '3px 10px',
            background: tone.bg, color: tone.fg,
            borderRadius: 'var(--r-pill)',
            fontSize: 10.5, fontWeight: 800,
            letterSpacing: '.04em', textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {tx(SEARCH_MODULE_LABEL[e.module])}
        </span>
      </div>
    </div>
  );
}

function EmptyHint({ loading }: { loading: boolean }) {
  const tx = useTextos(textosBusqueda);
  return (
    <div
      className="text-center"
      style={{ padding: '32px 24px', color: 'var(--muted-ink)' }}
    >
      <SearchIcon className="mx-auto w-7 h-7 mb-2" style={{ color: 'var(--muted-2)' }} />
      <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}>
        {loading ? tx('buscando') : tx('empiezaAEscribir')}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 500, marginTop: 4 }}>
        {tx('ayuda')}
      </div>
    </div>
  );
}

function Hotkey({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span className="inline-flex items-center" style={{ gap: 6 }}>
      <span
        className="inline-flex items-center justify-center"
        style={{
          width: 22, height: 22, borderRadius: 6,
          background: 'var(--surface)', border: '1.5px solid var(--line)',
          color: 'var(--muted-ink)',
        }}
      >
        <Icon className="w-3 h-3" strokeWidth={2.4} />
      </span>
      <span>{label}</span>
    </span>
  );
}
